import React, { ComponentProps, useMemo } from 'react';

import { PurchaseOrderLine } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import getProductDP from '../../utils/getProductDP';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import headerAttributeQuery from '../../graphql/queries/purchases/PurchaseAttributeConfigListQuery.graphql';
import productAttributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import Workspace from '../../components/Workspace';

import { Direction, FilterOperation, IPurchaseOrderLine, ProductClass } from '../../graphql/schema/p4types';

import { Paths } from '../../@types/keyOfNested';
import { useAbstractPurchaseLineColumnOverrides } from '../../utils/abstractPurchasesHooks';
import ProductFormModalFormatter from '../../components/modals/formatters/ProductFormModalFormatter';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { usePrecision } from '../../utils/hooks';

const LazySupplierForm: any = React.lazy(() => import('../../views/supplier/SupplierForm'));

const { formUrl, type, title, workspace } = PurchaseOrderLine;

const PURCHASE_ORDER_LINE_FIELD_NAMES: Array<Paths<IPurchaseOrderLine>> = [
  'purchaseOrder.orderNumber',
  'purchaseOrder.orderDate',
  'purchaseOrder.supplier.reference',
  'product.reference',
  'quantity',
  'received',
  'purchaseOrder.targetDate',
  'purchaseOrderLineStatus',
];

const PURCHASE_ORDER_LINE_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  'purchaseOrder.orderNumber': {
    name: 'Order',
    width: 100,
  },
  'purchaseOrder.orderDate': {
    name: 'Order Date',
  },
  'purchaseOrder.targetDate': {
    name: 'Exp. Delivery Date',
  },
  'purchaseOrder.supplier.reference': {
    name: 'Supplier',
    formatter: ModalFormatter,
    options: {
      modalContent: LazySupplierForm,
      modalProps: { forceReadOnly: true },
      entityId: 'purchaseOrder.supplier.id',
    },
  },
  'product.reference': {
    name: 'Product',
    formatter: ProductFormModalFormatter,
    options: {
      modalProps: { forceReadOnly: true },
      entityId: 'product.id',
    },
  },
  activeSellingUnit: {
    name: 'Unit',
  },
  grossSellingPrice: {
    name: 'Cost Price',
  },
  singleUnitGrossSellingPrice: {
    name: 'Single Unit Cost Price',
  },
  standardUnitNettSellingPrice: {
    name: 'Standard Unit Nett Cost Price',
  },
  quantity: {
    formatter: 'numeric',
    options: {
      format: { decimals: getProductDP },
    },
  },
  nettSellingPrice: {
    name: 'Nett Cost Price',
  },
  purchaseOrderLineStatus: {
    name: 'Status',
    sortable: false,
  },
  linePackagingVolume: {
    formatter: Formatters.NumericFormatter,
    options: { format: { decimals: 9 }, suppressTrailingZeros: true, suppressZeroes: true },
  },
  'purchaseOrder.currency.description': {
    name: 'Currency',
  },
  'product.vatProductCategory.description': {
    name: 'Product - VAT Product Category',
  },
  'vatProductCategory.description': {
    name: 'VAT Product Category',
  },
  'assignments.salesOrderLine.salesOrder.orderNumber': {
    name: 'Sales Assignments',
    formatter: Formatters.GenericListFormatter,
  },
};

const usePurchaseOrderLineCustomColumns = () => {
  const headerAttributeColumns = useAttributesForWorkspace(headerAttributeQuery, {
    columnKey: 'purchaseOrder.attributeValues.attributeConfig.title',
    valuesPath: 'purchaseOrder.attributeValues',
  });

  const productAttributeColumns = useAttributesForWorkspace(productAttributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  return useMemo(
    () => ({ ...headerAttributeColumns, ...productAttributeColumns }),
    [headerAttributeColumns, productAttributeColumns]
  );
};

export const usePurchaseOrderLineColumnOverrides = () => {
  const costPricePrecision = usePrecision('cost');
  const columnOverrides = useMemo(() => {
    const pricePrecisionOverrides = {
      grossSellingPrice: {
        name: 'Cost Price',
        options: { format: { decimals: costPricePrecision } },
      },
      singleUnitGrossSellingPrice: {
        name: 'Single Unit Cost Price',
        options: { format: { decimals: costPricePrecision } },
      },
      nettSellingPrice: {
        name: 'Nett Cost Price',
        options: { format: { decimals: costPricePrecision } },
      },
    };

    return { ...PURCHASE_ORDER_LINE_COLUMN_OVERRIDES, ...pricePrecisionOverrides };
  }, [costPricePrecision]);

  return useAbstractPurchaseLineColumnOverrides(columnOverrides);
};

const PurchaseOrderLineWorkspace = (props: WorkspaceConfig<IPurchaseOrderLine> & ComponentProps<typeof Workspace>) => {
  const columnOverrides = usePurchaseOrderLineColumnOverrides();
  const customColumns = usePurchaseOrderLineCustomColumns();

  const workspaceProps = useWorkspace<IPurchaseOrderLine>(props, PurchaseOrderLine, {
    columns: props.columnData || PURCHASE_ORDER_LINE_FIELD_NAMES,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: props.initialSortColumn || 'purchaseOrder.orderNumber',
    initialSortDirection: props.initialSortDir || Direction.Desc,
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.FreeType,
        },
      },
    ],
  });

  return (
    <Workspace
      defaultQuery={workspace}
      entityType={type}
      formIdOverride={`purchaseOrder.id`}
      formUrl={formUrl}
      title={title}
      {...workspaceProps}
      {...props}
      entity={PurchaseOrderLine}
    />
  );
};

export default PurchaseOrderLineWorkspace;
